import { ActionIcon, Badge, Stack, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { Role, useDeleteUserMutation, useGetUsersQuery, User } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/usuarios/")({
  component: RouteComponent,
  beforeLoad: requireRoles({ allowed: [Role.Administrador], fallback: "/403" })
});

function RouteComponent() {
  const navigate = useNavigate();
  const { data = [], isFetching, refetch } = useGetUsersQuery();
  const [deleteUser, { isLoading }] = useDeleteUserMutation();

  const handleDeleteUser = async (id: number) => {
    modals.openConfirmModal({
      title: "Confirmação de Exclusão",
      children: <p className="text-center">Tem certeza que deseja excluir este usuário? Esta ação não poderá ser desfeita.</p>,
      labels: { confirm: "Excluir", cancel: "Cancelar" },
      groupProps: { className: cn("grid! grid-cols-1 md:grid-cols-2") },
      cancelProps: { disabled: isLoading },
      confirmProps: { color: "red", loading: isLoading },
      onConfirm: async () => {
        await deleteUser({ id });
      }
    });
  };

  const columns: GridColDef<User>[] = [
    { field: "id", headerName: "ID", type: "number", align: "center", headerAlign: "center" },
    { field: "name", headerName: "Nome", minWidth: 150, flex: 1, type: "string" },
    { field: "email", headerName: "Email", minWidth: 150, flex: 1, type: "string" },
    { field: "role", headerName: "Cargo", minWidth: 150, type: "string" },
    { field: "company", headerName: "Empresa", minWidth: 120, type: "string", valueGetter: (val, { company }) => company?.name },
    {
      field: "isEnabled",
      headerName: "Status",
      minWidth: 100,
      type: "actions",
      renderCell: ({ value }) => {
        const badgeValues = {
          color: value ? "green" : "red",
          text: value ? "Ativo" : "Inativo"
        };

        return (
          <Badge color={badgeValues.color} variant="light">
            {badgeValues.text}
          </Badge>
        );
      }
    },
    {
      field: "isRoot",
      headerName: "Root",
      minWidth: 100,
      type: "boolean",
      align: "center",
      headerAlign: "center"
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      minWidth: 100,
      renderCell: ({ row }) => {
        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Usuário">
              <ActionIcon variant="light" color="yellow" onClick={() => navigate({ to: "/dashboard/usuarios/editar/$id/modal", params: { id: row.id } })}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Excluir Usuário">
              <ActionIcon variant="light" color="red" onClick={() => handleDeleteUser(row.id)}>
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between">
        <DashboardComponents.Title>Gestão de Usuários</DashboardComponents.Title>

        <div className="flex flex-1 justify-end gap-2 max-md:*:flex-1!">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Usuários" }} onClick={refetch} loading={isFetching} />

          <Tooltip label="Cadastrar novo Usuário">
            <ActionIcon size="xl" onClick={() => navigate({ to: "/dashboard/usuarios/cadastrar/modal" })}>
              <PlusIcon />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      <DataGridBase rows={data} columns={columns} loading={isFetching} />
    </Stack>
  );
}
